// WarehouseList.jsx — proc-v3 (F2.3f): SETTIMA VISTA DEL MONOLITE in React,
// "Magazzino", flag-gated (shell_proc_v3_warehouse, default OFF).
//
// Replica 1:1 renderWarehouse di proc-v0.js:11597-11751 — vista a 5 TAB:
//  - "Giacenze (live)"     → DataTable condiviso (foto, prodotto+categoria,
//                            area, giacenza con unità e alternativa, età).
//  - "Entrate / Uscite"    → DataTable (11 colonne, badge ENTRATA/USCITA,
//                            elimina movimento CONFERMATO, foto lightbox) o
//                            resa RAGGRUPPATA per codice (toggle v1).
//  - "DDT & Partite"       → DELEGA il mount al modulo v1 window.WarehouseDDT
//                            (stesso fallback del v1 se non caricato).
//  - "Ricevimenti ordini"  → 2 DataTable (ordini pendenti con "Ricevi",
//                            ricevute recenti con click → dettaglio ordine v1).
//  - "Aree"                → card-list con rinomina (prompt v1 + sync API).
// STATO CONDIVISO v1↔v3: tab attivo = window._whTab, raggruppamento log =
// window._whGroup (STESSI globali del monolite: il flip di flag conserva il
// tab). Ogni cambio tab/toggle passa da win.renderWarehouse() (sotto flag è
// il wrapper v3): stesso ciclo redraw del v1, dati riletti dallo store.
//
// La vista v1 NON è selectable e non ha bulk: nemmeno qui. Le scritture
// (elimina movimento, rinomina area) usano le PRIMITIVE del monolite
// (confirmWrite/store/api) ⇒ payload identici. Modali entrata/uscita/sposta/
// ricevi = V1 (deleghe in api.js). MAI 'nav:badge' da questo modulo.

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import {
  tWh, WH_TEXTS,
  whTab, whSetTab, whGroup, whSetGroup,
  readWhCounts, readWhLive, readWhLog, readWhOrders, readWhAreas, groupWhLog,
  whLiveQtyParts, whLogQtyParts, whPrettyDate, whPrettyAgo,
  whLiveNameFilterText, whLiveLocationFilterText, whLiveQtyFilterText, whLiveLastFilterText,
  whLogDateFilterText, whLogDirectionFilterText, whLogNameFilterText, whLogQtyFilterText,
  whLogLocationFilterText, whLogDestFilterText, whLogSourceFilterText, whLogCodeFilterText, whLogNotesFilterText,
  whPendingExpectedFilterText, whPendingStatusFilterText, whRecentDateFilterText,
  ordStatusBadgeClass, ordStatusLabel,
  whOpenIn, whOpenOut, whOpenTransfer, whOpenReceive, whOpenOrderDetail,
  whOpenMovementPhotos, whOpenReceiptPhoto, whDeleteMovement, whRenameArea,
} from './api.js';

// Badge direzione (v1:11675) — condiviso tra log piatto e raggruppato.
function DirBadge({ dir }) {
  return dir === 'in'
    ? <span className="bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5 rounded">{WH_TEXTS.logIn}</span>
    : <span className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 rounded">{WH_TEXTS.logOut}</span>;
}

// Cella Q.tà del log (v1:11642): 'inserito → convertito' se l'unità è alternativa.
function LogQtyCell({ r, ctx }) {
  const p = whLogQtyParts(r);
  if (!p.entered) return <ctx.Mark text={p.base} tokens={ctx.tokens} />;
  return (
    <>
      <span title={WH_TEXTS.logEnteredTitle}><ctx.Mark text={p.entered} tokens={ctx.tokens} /></span>
      {' '}
      <span className="text-slate-400">→ <ctx.Mark text={p.base} tokens={ctx.tokens} /></span>
    </>
  );
}

// ---- Tab "Giacenze (live)" (v1:11623-11632) ---------------------------------
function WhLive({ win, rows }) {
  const columns = [
    {
      key: '_img',
      label: WH_TEXTS.livePhotoCol,
      sortable: true,
      sortValue: (r) => r._v1Rank._img, // rank v1
      filter: false,
      render: (r) => (r._img
        ? <img src={r._img} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} alt="" />
        : <div style={{ width: 40, height: 40, borderRadius: 6, background: '#f1f5f9', border: '1px solid #e2e8f0' }} />),
    },
    {
      key: '_name',
      label: WH_TEXTS.liveProductCol,
      sortable: true,
      sortValue: (r) => r._v1Rank._name, // rank v1
      filterText: whLiveNameFilterText,
      render: (r, ctx) => (
        <>
          <span className="font-medium"><ctx.Mark text={r._name} tokens={ctx.tokens} /></span>
          {r._cat ? <div className="text-[10px] text-slate-400 font-mono"><ctx.Mark text={r._cat} tokens={ctx.tokens} /></div> : null}
        </>
      ),
    },
    {
      key: 'location',
      label: WH_TEXTS.liveLocationCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.location, // rank v1
      filterText: whLiveLocationFilterText,
      render: (r, ctx) => (r.location
        ? <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs"><ctx.Mark text={r.location} tokens={ctx.tokens} /></code>
        : <span className="text-slate-400 italic text-xs">{WH_TEXTS.liveNotAllocated}</span>),
    },
    {
      key: 'qty',
      label: WH_TEXTS.liveQtyCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.qty, // rank v1 (type number)
      filterText: whLiveQtyFilterText,
      render: (r, ctx) => {
        const p = whLiveQtyParts(r);
        return (
          <>
            <span className={'font-bold' + (p.negative ? ' text-rose-600' : '')}><ctx.Mark text={p.q + ' ' + p.unit} tokens={ctx.tokens} /></span>
            {p.alt ? <> <span className="text-[10px] text-slate-500"><ctx.Mark text={p.alt} tokens={ctx.tokens} /></span></> : null}
          </>
        );
      },
    },
    {
      key: 'lastAt',
      label: WH_TEXTS.liveLastCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.lastAt, // rank v1 (type date)
      filterText: (r) => whLiveLastFilterText(win, r),
      render: (r, ctx) => <span className="text-xs"><ctx.Mark text={whPrettyAgo(win, r.lastAt)} tokens={ctx.tokens} /></span>,
    },
  ];
  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3" id="wh-live-mount">
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => (r.productId || '') + '|' + (r.location || '')}
        sort={{ key: '_name', dir: 'asc' }}
        filterable
        emptyMessage={WH_TEXTS.liveEmpty}
      />
    </div>
  );
}

// ---- Tab "Entrate / Uscite" (v1:11633-11687) --------------------------------
function WhLog({ win, rows }) {
  const grouped = whGroup(win);
  const columns = [
    {
      key: 'at',
      label: WH_TEXTS.logDateCol, // HARDCODED nel v1 (v1:11674), NON dal dict
      sortable: true,
      sortValue: (r) => r._v1Rank.at, // rank v1 (type date)
      filterText: whLogDateFilterText,
      render: (r, ctx) => <span className="text-xs"><ctx.Mark text={whPrettyDate(r.at).slice(0, 16)} tokens={ctx.tokens} /></span>,
    },
    {
      key: 'direction',
      label: WH_TEXTS.logTypeCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.direction, // rank v1
      filterText: whLogDirectionFilterText,
      render: (r) => <DirBadge dir={r.direction} />,
    },
    {
      key: '_name',
      label: WH_TEXTS.liveProductCol,
      sortable: true,
      sortValue: (r) => r._v1Rank._name, // rank v1
      filterText: whLogNameFilterText,
      render: (r, ctx) => (
        <div className="flex items-center gap-2">
          {r._img ? <img src={r._img} style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} alt="" /> : null}
          <span><ctx.Mark text={r._name} tokens={ctx.tokens} /></span>
        </div>
      ),
    },
    {
      key: 'qty',
      label: WH_TEXTS.logQtyCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.qty, // rank v1 (type number)
      filterText: whLogQtyFilterText,
      render: (r, ctx) => <LogQtyCell r={r} ctx={ctx} />,
    },
    {
      key: 'location',
      label: WH_TEXTS.logAreaCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.location, // rank v1
      filterText: whLogLocationFilterText,
      render: (r, ctx) => (r.location ? <ctx.Mark text={r.location} tokens={ctx.tokens} /> : '—'),
    },
    {
      key: 'destination',
      label: WH_TEXTS.logDestCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.destination, // rank v1
      filterText: whLogDestFilterText,
      render: (r, ctx) => (r.destination ? <ctx.Mark text={r.destination} tokens={ctx.tokens} /> : '—'),
    },
    {
      key: 'source',
      label: WH_TEXTS.logSourceCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.source, // rank v1
      filterText: whLogSourceFilterText,
      render: (r, ctx) => (r.source ? <span className="text-xs"><ctx.Mark text={r.source} tokens={ctx.tokens} /></span> : '—'),
    },
    {
      key: 'batchCode',
      label: WH_TEXTS.logCodeCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.batchCode, // rank v1
      filterText: whLogCodeFilterText,
      render: (r, ctx) => (r.batchCode ? <code className="bg-slate-100 px-1 rounded text-[10px]"><ctx.Mark text={r.batchCode} tokens={ctx.tokens} /></code> : ''),
    },
    {
      key: 'notes',
      label: WH_TEXTS.logNotesCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.notes, // rank v1
      filterText: whLogNotesFilterText,
      render: (r, ctx) => (r.notes ? <span className="text-xs"><ctx.Mark text={r.notes} tokens={ctx.tokens} /></span> : ''),
    },
    {
      key: '_nphotos',
      label: WH_TEXTS.logPhotosCol,
      sortable: true,
      sortValue: (r) => r._v1Rank._nphotos, // rank v1 (quirk: confronto STRINGA nel v1)
      filter: false,
      render: (r) => (r._nphotos
        ? <button type="button" className="wh-mov-photos text-cyan-700 hover:underline text-xs" onClick={() => whOpenMovementPhotos(win, r)}>{r._nphotos + ' foto'}</button>
        : ''),
    },
    {
      key: '_del',
      label: '',
      sortable: true,
      sortValue: (r) => r._v1Rank._del, // rank v1 (colonna senza valori: ordine base)
      filter: false,
      render: (r) => (
        <button type="button" className="wh-mov-del text-rose-500 hover:text-rose-700" title={WH_TEXTS.logDelTitle} onClick={() => whDeleteMovement(win, r.id)}>×</button>
      ),
    },
  ];
  const groups = grouped ? groupWhLog(rows) : null;
  return (
    <>
      <div className="flex items-center justify-end mb-2">
        <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
          <input
            type="checkbox"
            id="wh-group-toggle"
            checked={grouped}
            onChange={(e) => { whSetGroup(win, e.target.checked); win.renderWarehouse(); }}
          />
          {' ' + WH_TEXTS.groupToggle}
        </label>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3" id="wh-log-mount">
        {grouped ? (
          groups.length ? groups.map((g) => (
            <div className="border border-slate-200 rounded-lg mb-3 overflow-hidden" key={g.k}>
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 flex-wrap">
                <div className="flex items-center gap-2">
                  {g.isNo
                    ? <span className="font-medium text-slate-500 italic">{WH_TEXTS.logNoGroup}</span>
                    : (
                      <>
                        <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{g.k}</code>
                        {' '}
                        {g.type === 'mixed'
                          ? <span className="bg-slate-100 text-slate-600 text-[10px] px-1.5 py-0.5 rounded">{WH_TEXTS.logMixed}</span>
                          : <DirBadge dir={g.type} />}
                      </>
                    )}
                </div>
                {/* nodo-spazio: il template v1 (v1:11657-11661) ha newline+indent
                    tra i due div ⇒ il textContent contiene whitespace; in flex il
                    text node di soli spazi NON si rende — parità solo testuale */}
                {' '}
                <div className="text-xs text-slate-500">
                  {whPrettyDate(g.last).slice(0, 16) + ' · ' + g.items.length + ' righe'
                    + (g.dest ? ' · → ' + g.dest : '') + (g.src ? ' · fonte ' + g.src : '')}
                </div>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {g.items.map((r) => {
                    const p = whLogQtyParts(r);
                    return (
                      <tr className="border-t" key={r.id}>
                        <td className="px-3 py-1.5">{r._name}</td>
                        <td className="px-3 py-1.5">
                          {p.entered
                            ? <><span title={WH_TEXTS.logEnteredTitle}>{p.entered}</span> <span className="text-slate-400">{'→ ' + p.base}</span></>
                            : p.base}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-slate-500">{r.location ? r.location : '—'}</td>
                        <td className="px-3 py-1.5 text-right">
                          <button type="button" className="wh-mov-del text-rose-500 hover:text-rose-700" title={WH_TEXTS.logDelTitle} onClick={() => whDeleteMovement(win, r.id)}>×</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )) : <div className="text-center text-slate-400 italic py-6">{WH_TEXTS.logEmpty}</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey="id"
            sort={{ key: 'at', dir: 'desc' }}
            filterable
            emptyMessage={WH_TEXTS.logEmpty}
          />
        )}
      </div>
    </>
  );
}

// ---- Tab "DDT & Partite" (v1:11718-11721) -----------------------------------
// DELEGA: il modulo v1 window.WarehouseDDT monta nel container che gli diamo
// (stesso mount del v1, ri-eseguito a ogni redraw come fa renderWarehouse).
// React possiede SOLO il div: i figli sono del modulo (innerHTML suo).
function WhDdt({ win, tick }) {
  const ref = React.useRef(null);
  const has = !!win.WarehouseDDT;
  React.useEffect(() => {
    if (has && ref.current) win.WarehouseDDT.mount(ref.current);
  }, [win, tick, has]);
  if (!has) {
    return <div className="text-slate-400 italic p-6 text-center bg-white rounded-lg border border-slate-200">{WH_TEXTS.ddtFallback}</div>;
  }
  return <div id="wh-ddt-mount" ref={ref} />;
}

// ---- Tab "Ricevimenti ordini" (v1:11722-11749) ------------------------------
function WhOrders({ win, pendingRows, recentRows }) {
  const pendingCols = [
    {
      key: 'code',
      label: tWh(win, 'col.po_number'),
      sortable: true,
      sortValue: (r) => r._v1Rank.code, // rank v1
      render: (r, ctx) => <span className="font-medium"><ctx.Mark text={String(r.code == null ? '' : r.code)} tokens={ctx.tokens} /></span>,
    },
    {
      key: '_supplierName',
      label: tWh(win, 'col.supplier'),
      sortable: true,
      sortValue: (r) => r._v1Rank._supplierName, // rank v1
    },
    {
      key: '_lines',
      label: tWh(win, 'col.lines'),
      sortable: true,
      sortValue: (r) => r._v1Rank._lines, // rank v1 (type number)
    },
    {
      key: 'status',
      label: tWh(win, 'col.status'),
      sortable: true,
      sortValue: (r) => r._v1Rank.status, // rank v1 (stato RAW)
      filterText: whPendingStatusFilterText,
      render: (r, ctx) => (
        <span className={ordStatusBadgeClass(r.status) + ' text-xs px-2 py-0.5 rounded'}>
          <ctx.Mark text={ordStatusLabel(r.status)} tokens={ctx.tokens} />
        </span>
      ),
    },
    {
      key: 'expectedDate',
      label: WH_TEXTS.ordersExpectedCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.expectedDate, // rank v1 (type date)
      filterText: whPendingExpectedFilterText,
      render: (r, ctx) => <ctx.Mark text={whPendingExpectedFilterText(r)} tokens={ctx.tokens} />,
    },
    {
      key: '_act',
      label: '',
      sortable: true,
      sortValue: (r) => r._v1Rank._act, // rank v1 (colonna senza valori: ordine base)
      filter: false,
      render: (r) => (
        <button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white px-2 py-1 rounded text-xs wh-recv-btn" onClick={() => whOpenReceive(win, r.id)}>{WH_TEXTS.ordersReceiveBtn}</button>
      ),
    },
  ];
  const recentCols = [
    {
      key: 'receivedAt',
      label: tWh(win, 'col.date'),
      sortable: true,
      sortValue: (r) => r._v1Rank.receivedAt, // rank v1 (type date)
      filterText: whRecentDateFilterText,
      render: (r, ctx) => <ctx.Mark text={whRecentDateFilterText(r)} tokens={ctx.tokens} />,
    },
    {
      key: '_po',
      label: tWh(win, 'col.po_number'),
      sortable: true,
      sortValue: (r) => r._v1Rank._po, // rank v1
    },
    {
      key: '_supplier',
      label: tWh(win, 'col.supplier'),
      sortable: true,
      sortValue: (r) => r._v1Rank._supplier, // rank v1
    },
    {
      key: 'ddtNumber',
      label: WH_TEXTS.ordersDdtCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.ddtNumber, // rank v1
    },
    {
      key: '_itemCount',
      label: WH_TEXTS.ordersItemsCol,
      sortable: true,
      sortValue: (r) => r._v1Rank._itemCount, // rank v1 (type number)
    },
    {
      key: '_hasPhoto',
      label: WH_TEXTS.ordersPhotoCol,
      sortable: true,
      sortValue: (r) => r._v1Rank._hasPhoto, // rank v1
      filter: false,
      render: (r) => (r._hasPhoto
        ? <button type="button" className="text-cyan-700 hover:underline text-xs rcp-photo" onClick={() => whOpenReceiptPhoto(win, r.photoDataUrl)}>{WH_TEXTS.ordersPhotoView}</button>
        : '—'),
    },
  ];
  return (
    <>
      <h2 className="font-semibold text-base mb-2">{WH_TEXTS.ordersPendingH}</h2>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 mb-5" id="wh-pending-mount">
        <DataTable
          columns={pendingCols}
          rows={pendingRows}
          rowKey="id"
          sort={{ key: 'expectedDate', dir: 'asc' }}
          filterable
          emptyMessage={WH_TEXTS.ordersPendingEmpty}
        />
      </div>
      <h2 className="font-semibold text-base mb-2">{WH_TEXTS.ordersRecentH}</h2>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3" id="wh-recent-mount">
        <DataTable
          columns={recentCols}
          rows={recentRows}
          rowKey="id"
          sort={{ key: 'receivedAt', dir: 'desc' }}
          filterable
          onRowClick={(row) => whOpenOrderDetail(win, row.orderId)}
          emptyMessage={WH_TEXTS.ordersRecentEmpty}
        />
      </div>
    </>
  );
}

// ---- Tab "Aree" (v1:11688-11717) --------------------------------------------
function WhAreas({ win, areas }) {
  if (!areas.length) {
    return <div className="text-center text-slate-400 italic py-6 bg-white rounded-lg border border-slate-200">{WH_TEXTS.areasEmpty}</div>;
  }
  return (
    <div className="space-y-3">
      {areas.map((a) => (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" key={a.area || '__empty__'}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-semibold">{a.area ? a.area : <span className="text-slate-400 italic">{WH_TEXTS.areasNotAllocated}</span>}</div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">{a.items.length + ' prodotti · ' + a.tot + ' pz tot.'}</span>
              <button type="button" className="wh-area-rename text-cyan-700 hover:underline text-xs" onClick={() => whRenameArea(win, a.area)}>{WH_TEXTS.areasRename}</button>
            </div>
          </div>
          {a.items.length ? (
            <table className="w-full text-sm mt-2">
              <thead className="text-[10px] uppercase text-slate-500">
                <tr><th className="text-left py-1">{WH_TEXTS.areasProductCol}</th><th className="text-right py-1">{WH_TEXTS.areasQtyCol}</th></tr>
              </thead>
              <tbody>
                {a.items.map((x, i) => (
                  <tr className="border-t" key={i}>
                    <td className="py-1">{x.name}</td>
                    <td className="py-1 text-right font-medium">{x.qty + ' ' + x.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="text-[11px] text-slate-400 italic mt-2">{WH_TEXTS.areasEmptyArea}</div>}
        </div>
      ))}
    </div>
  );
}

// ---- Vista completa ----------------------------------------------------------
export function WarehouseList({ win, tick }) {
  const tab = whTab(win); // window._whTab: GLOBALE condiviso col v1
  const counts = React.useMemo(() => readWhCounts(win), [win, tick]);
  const live = React.useMemo(() => (tab === 'live' ? readWhLive(win) : null), [win, tick, tab]);
  const log = React.useMemo(() => (tab === 'log' ? readWhLog(win) : null), [win, tick, tab]);
  const orders = React.useMemo(() => (tab === 'orders' ? readWhOrders(win) : null), [win, tick, tab]);
  const areas = React.useMemo(() => (tab === 'areas' ? readWhAreas(win) : null), [win, tick, tab]);

  const tabBtn = (key, label) => (
    <button
      key={key}
      type="button"
      className={'wh-tab px-3 py-1.5 rounded text-sm ' + (tab === key ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50')}
      onClick={() => { whSetTab(win, key); win.renderWarehouse(); }}
    >{label}</button>
  );

  let content;
  if (tab === 'live') content = <WhLive win={win} rows={live.rows} />;
  else if (tab === 'log') content = <WhLog win={win} rows={log.rows} />;
  else if (tab === 'areas') content = <WhAreas win={win} areas={areas} />;
  else if (tab === 'ddt') content = <WhDdt win={win} tick={tick} />;
  else content = <WhOrders win={win} pendingRows={orders.pendingRows} recentRows={orders.recentRows} />;

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">{tWh(win, 'wh.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{counts.stockLines + ' righe di giacenza · ' + counts.movements + ' movimenti'}</p>
        </div>
        <div className="flex gap-2">
          <button id="wh-in" type="button" className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-sm font-medium" onClick={() => whOpenIn(win)}>{WH_TEXTS.btnIn}</button>
          <button id="wh-out" type="button" className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded text-sm font-medium" onClick={() => whOpenOut(win)}>{WH_TEXTS.btnOut}</button>
          <button id="wh-move" type="button" className="bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-1.5 rounded text-sm font-medium" onClick={() => whOpenTransfer(win)}>{WH_TEXTS.btnMove}</button>
        </div>
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {tabBtn('live', WH_TEXTS.tabLive)}
        {tabBtn('log', WH_TEXTS.tabLog)}
        {tabBtn('ddt', WH_TEXTS.tabDdt)}
        {tabBtn('orders', WH_TEXTS.tabOrders)}
        {tabBtn('areas', WH_TEXTS.tabAreas)}
      </div>
      <div id="wh-tab-content">{content}</div>
    </div>
  );
}

// Factory (pattern createProcV3Orders): il register/install la aggancia al
// WRAP di window.renderWarehouse. render() idempotente sul container;
// unmount() libera il container (il monolite resta vivo per le altre viste).
export function createProcV3Warehouse(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('warehouse-content');
    if (!container) throw new Error('#warehouse-content assente');
    if (!root || mountedEl !== container || !container.isConnected) {
      if (root) { try { root.unmount(); } catch (e) { /* già smontato */ } }
      container.replaceChildren(); // eventuali resti del v1 (catch-up)
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1; // ogni chiamata del wrapper = refresh dati (modali, delete, tab)
    root.render(<WarehouseList win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
